"use client";

import { Icon, deviceDetector, ospThemes } from "@ospweb/sdk-ui";
import React, { FC, useMemo } from "react";

import { cn } from "utils";
import { AddAssetsModalCreateAble } from "../AddAssets";
import NextImage from "../../NextImage";
import { StartedWithPortfolioProps } from "./types";
import { I18nUtil } from "@utils/i18n";
import { PositionBroker } from "@/src/features/api/memberApi";
import Translations from "@translate/components/CoPilot/StartedWithPortfolio/StartedWithPortfolio";
import { useGetPositionBrokers } from "@/src/hooks/useGetPositionBrokers";

const I18n = I18nUtil.createScoped(Translations);

interface DescriptionAreaProps {
  type: "assets" | "sync";
  className?: string;
  isNonMemberOrExpired?: boolean;
  isMobile?: boolean;
  onCompleted?: () => void;
}
interface IconProps {
  icon: string;
  name: string;
}
interface GoSyncCardProps {
  type: "crypto" | "stock";
  icons?: IconProps[];
  onClick?: () => void;
  isMobile?: boolean;
}

const DescriptionArea: FC<DescriptionAreaProps> = ({
  type,
  className,
  isMobile = false,
  onCompleted,
}) => {
  return (
    <div
      className={cn("flex flex-row items-center justify-between", className)}
    >
      <div className={cn("flex flex-row", isMobile ? "items-center" : "")}>
        <div className="flex items-center justify-center w-10 h-10 min-w-10 rounded-2 bg-background_brand_overlay10">
          <Icon
            name={type === "assets" ? "addwatchlist" : "import portfolio"}
            size={24}
            color={ospThemes.colors.content_brand_primary}
          />
        </div>
        <div className="flex flex-col ml-3">
          <div className="flex flex-row items-center">
            <span className="title_l text-content_primary">
              {type === "assets"
                ? I18n.t("Add Assets Manually")
                : I18n.t("Sync Your Accounts")}
            </span>
            {type === "sync" && (
              <span className="h-3 body_bold_xs text-content_inverse_primary bg-background_brand_overlay100 rounded-1 px-1 ml-1">
                {I18n.t("Premium")}
              </span>
            )}
          </div>
          <span className="body_s text-content_secondary">
            {type === "assets"
              ? I18n.t(
                  "Enter your stocks and crypto yourself and I'll track it all for you.",
                )
              : I18n.t(
                  "Connect your broker or wallet and I'll import everything automatically.",
                )}
          </span>
        </div>
      </div>
      {type === "assets" && (
        <div
          data-openreplay-label={`StartedWithPortfolio · StartedWithPortfolio · div | ${isMobile ? I18n.t("Create") : I18n.t("Add Assets")}`}
          className={cn(
            "button sm_button button_m text-content_primary bg-background_white_overlay10",
            isMobile ? "w-17 ml-3" : "w-24",
          )}
          onClick={async () => {
            await AddAssetsModalCreateAble({
              isMobile,
              onCompleted: onCompleted,
            });
          }}
        >
          {isMobile ? I18n.t("Create") : I18n.t("Add Assets")}
        </div>
      )}
    </div>
  );
};

const GoSyncCard: FC<GoSyncCardProps> = ({
  type,
  onClick,
  isMobile = false,
  icons = [],
}) => {
  return (
    <div
      data-openreplay-label="StartedWithPortfolio · StartedWithPortfolio · div"
      className={cn(
        "flex flex-col items-center justify-between bg-background_solid_tertiary rounded-3 p-4 cursor-pointer",
        isMobile ? "w-full" : "min-w-[232px]",
      )}
      onClick={onClick}
    >
      {isMobile ? (
        <div className="flex flex-col w-full justify-between">
          <div className="flex flex-row justify-between items-center">
            <span
              className={cn(
                "text-content_primary",
                isMobile ? "title_m" : "title_l",
              )}
            >
              {type === "crypto" ? I18n.t("Crypto") : I18n.t("Stock")}
            </span>
            <Icon
              name="right-arrow-alt"
              size={16}
              color={ospThemes.colors.content_primary}
            />
          </div>
          <div className="flex flex-row mt-3 gap-[3px]">
            {icons.map((icon) => (
              <NextImage
                src={icon.icon}
                alt={icon.name}
                width={20}
                height={20}
                className="w-5 h-5 rounded-full border-[0.6px] border-border_solid overflow-hidden"
                key={icon.name}
              />
            ))}
          </div>
        </div>
      ) : (
        <div className="flex flex-row w-full justify-between">
          <span
            className={cn(
              "text-content_primary",
              isMobile ? "title_m" : "title_l",
            )}
          >
            {type === "crypto" ? I18n.t("Crypto") : I18n.t("Stock")}
          </span>
          <div className="flex flex-row gap-[3px]">
            {icons.map((icon) => (
              <NextImage
                src={icon.icon}
                alt={icon.name}
                width={20}
                height={20}
                className="w-5 h-5 rounded-full border-[0.6px] border-border_solid overflow-hidden"
                key={icon.name}
              />
            ))}
          </div>
        </div>
      )}
      <span className="body_s text-content_secondary mt-3">
        {type === "crypto"
          ? I18n.t("Link your wallet or exchange to pull in your tokens.")
          : I18n.t("Link your broker and I'll import all your positions.")}
      </span>
    </div>
  );
};

export const StartedWithPortfolio: FC<StartedWithPortfolioProps> = (props) => {
  const {
    className,
    onSkip,
    isNonMemberOrExpired = true,
    handleImportClick,
  } = props;
  const { data: positionBrokers } = useGetPositionBrokers();

  // 通过 category 字段筛选出 crypto 和 stock 数组
  // 每个数组最多包含 5 个元素，如果 category 中同时包含 stock 和 crypto，则在两个数组中都会添加
  const { cryptoIcons, stockIcons } = useMemo(() => {
    const crypto: IconProps[] = [];
    const stock: IconProps[] = [];

    positionBrokers?.forEach((broker: PositionBroker, index: number) => {
      const categories = broker.category || [];
      const hasCrypto = categories.includes("CRYPTO");
      const hasStock = categories.includes("STOCK");

      // 如果包含 crypto 且 crypto 数组未满 5 个，添加到 crypto 数组
      if (hasCrypto && crypto.length < 5 && broker.broker_icon) {
        crypto.push({
          icon: broker.broker_icon,
          name: broker.name || `broker_${index}`,
        });
      }

      // 如果包含 stock 且 stock 数组未满 5 个，添加到 stock 数组
      if (hasStock && stock.length < 5 && broker.broker_icon) {
        stock.push({
          icon: broker.broker_icon,
          name: broker.name || `broker_${index}`,
        });
      }
    });

    return {
      cryptoIcons: crypto,
      stockIcons: stock,
    };
  }, [positionBrokers]);

  const isMobile = useMemo(() => deviceDetector.isMobile(), []);

  return (
    <div
      className={cn(
        "flex flex-col gap-6 md:mt-[105px] max-w-[528px]",
        className,
      )}
      // data-tracking-scope="add-assets-content"
    >
      {/* 标题和描述区域 */}
      <div className="flex flex-col">
        {/* 标题 */}
        <h2
          aria-label="Started With Portfolio h2"
          data-openreplay-label="Started With Portfolio h2 Semantic"
          id="sem-started-with-portfolio-h2"
          className="md:headline_regular_l headline_regular_m text-content_primary"
        >
          {I18n.t("Get started with your first portfolio!")}
        </h2>
        {/* 描述 */}
        <p className="body_m text-content_secondary mt-2">
          {I18n.t(
            "Add your holdings so I can start building around you. I'll learn to think like you, and get smarter to keep you unstoppable.",
          )}
        </p>
        <DescriptionArea
          type="assets"
          className={cn(isMobile ? "mt-8" : "mt-12")}
          isMobile={isMobile}
          isNonMemberOrExpired={false}
        />
        <div className="w-full h-[1px] bg-border_white_transparent10 mt-6" />
        <DescriptionArea
          type="sync"
          className="mt-6"
          isMobile={isMobile}
          isNonMemberOrExpired={isNonMemberOrExpired}
        />
        <div
          className={cn(
            "flex flex-row gap-3 mt-5",
            isMobile ? "ml-0" : "ml-[52px]",
          )}
        >
          <GoSyncCard
            data-openreplay-label="StartedWithPortfolio · StartedWithPortfolio · GoSyncCard"
            type="stock"
            isMobile={isMobile}
            icons={stockIcons}
            onClick={() => void handleImportClick?.()}
          />
          <GoSyncCard
            data-openreplay-label="StartedWithPortfolio · StartedWithPortfolio · GoSyncCard"
            type="crypto"
            isMobile={isMobile}
            icons={cryptoIcons}
            onClick={() => void handleImportClick?.()}
          />
        </div>

        <div className="flex w-full justify-center items-center mt-12 cursor-pointer">
          <div
            data-openreplay-label={`StartedWithPortfolio · StartedWithPortfolio · div | ${I18n.t("Skip for now")}`}
            className="w-[101px] h-9 text-content_tertiary body_m flex items-center justify-center"
            onClick={onSkip}
          >
            {I18n.t("Skip for now")}
          </div>
        </div>
      </div>
    </div>
  );
};
